import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";

const TIERS = [
    {
        id: "T0",
        title: "Advisory trust signal",
        where: "Any environment - including vanilla public-chain tokens",
        how: "The envelope decision is consulted, not enforced. Venues, registrars and counterparties use it to decide what they recognise. For securities the legal record remains the registrar's book; the token is an execution surface.",
        intrusion: "None",
    },
    {
        id: "T1",
        title: "Key-holder gate",
        where: "The institution's own signing service - custodian or transfer-agent infrastructure",
        how: "No transaction exists until the key holder signs. The signing service requires a verified, unexpired, unconsumed KATLAS authorisation before it will sign. The institution gates itself, on its own systems.",
        intrusion: "None - client-side policy at the point where the private key already lives",
        demoed: true,
    },
    {
        id: "T2",
        title: "Token-contract compliance hook",
        where: "The token contract the issuer deploys",
        how: "Regulated tokenised securities are issued on standards built for transfer control - ERC-1400 canTransfer, ERC-3643 pluggable compliance. The existing hook checks an attested registry of envelope decisions, or a decision signature passed with the transfer.",
        intrusion: "None to the chain - issuer-level contract configuration, standard practice for regulated instruments",
    },
    {
        id: "T3",
        title: "Smart-account validation",
        where: "The holder's account (ERC-4337 account abstraction)",
        how: "Account-level validation logic requires a valid authorisation signature before executing the action.",
        intrusion: "None - account-deployed logic",
    },
    {
        id: "T4",
        title: "Ledger-native check",
        where: "Permissioned ledgers - Corda, Fabric, DAML, Swift's shared ledger",
        how: "The envelope decision is consumed as a first-class validity condition before the transaction is accepted.",
        intrusion: "None - the platform natively supports pre-transaction checks",
    },
];

export default function Enforcement() {
    return (
        <Layout>
            <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                Hybrid architecture · On-chain / off-chain split
            </p>
            <h1 className="mt-3 text-3xl font-medium tracking-tight text-slate-900">
                Where enforcement lives
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                No existing blockchain needs to be patched, and none is. A control
                decision never requires a mandatory checkpoint bolted onto a chain -
                because every realistic deployment already has a boundary where
                discretion exists. The gate is placed where a decision-maker already
                sits. The same envelope artifact is consumable at every tier; each
                execution environment chooses how strictly to consume it.
            </p>

            <div className="mt-8 space-y-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200">
                {TIERS.map((t) => (
                    <section
                        key={t.id}
                        className={`p-6 sm:p-8 ${t.demoed ? "bg-blue-950" : "bg-white"}`}
                        data-testid={`enforcement-tier-${t.id}`}
                    >
                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                            <span className={`font-mono text-sm ${t.demoed ? "text-blue-300" : "text-slate-400"}`}>
                                {t.id}
                            </span>
                            <h2 className={`text-lg font-medium tracking-tight ${t.demoed ? "text-white" : "text-slate-900"}`}>
                                {t.title}
                            </h2>
                            {t.demoed && (
                                <span className="rounded-sm bg-blue-800 px-2 py-0.5 font-mono text-[11px] uppercase tracking-wide text-blue-100">
                                    Demonstrated in this sandbox
                                </span>
                            )}
                        </div>
                        <dl className={`mt-3 grid gap-x-8 gap-y-2 text-sm leading-relaxed sm:grid-cols-[10rem_1fr] sm:pl-9 ${t.demoed ? "text-blue-100" : "text-slate-600"}`}>
                            <dt className={`font-mono text-xs uppercase tracking-wide ${t.demoed ? "text-blue-300" : "text-slate-400"}`}>Where</dt>
                            <dd>{t.where}</dd>
                            <dt className={`font-mono text-xs uppercase tracking-wide ${t.demoed ? "text-blue-300" : "text-slate-400"}`}>How</dt>
                            <dd>{t.how}</dd>
                            <dt className={`font-mono text-xs uppercase tracking-wide ${t.demoed ? "text-blue-300" : "text-slate-400"}`}>Chain intrusion</dt>
                            <dd>{t.intrusion}</dd>
                        </dl>
                    </section>
                ))}
            </div>

            <section className="mt-10 rounded-sm border border-slate-200 bg-white p-8" data-testid="enforcement-demo-note">
                <h2 className="text-lg font-medium tracking-tight text-slate-900">
                    What this sandbox demonstrates
                </h2>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
                    The execution adapter in this demo implements <span className="font-medium text-slate-900">Tier 1 -
                    the key-holder gate</span>. It independently verifies that a
                    presented authorisation is permitting, integrity-sealed, correctly
                    scoped, current, not superseded and never consumed before - then
                    records the synthetic action exactly once. Forged, expired,
                    wrong-event, replayed and superseded authorisations are rejected by
                    automated conformance tests. The same verification logic is what a
                    Tier&nbsp;2 compliance contract or a Tier&nbsp;4 ledger check would
                    run - the artifact and its checks are identical; only the hosting
                    boundary changes.
                </p>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                    The hybrid split follows from this: institutional decisions,
                    role-to-role repair and evidence references stay off-chain - where
                    authority, accountability and privacy live. Only a minimal,
                    verifiable, replay-proof authorisation crosses to the execution
                    boundary. Tokens stay sovereign; no environment is forced to
                    consume anything.
                </p>
                <p className="mt-5">
                    <Link
                        to="/live"
                        className="text-sm font-medium text-blue-950 underline underline-offset-2 hover:text-blue-800"
                    >
                        See the adapter verify, gate and consume a live decision →
                    </Link>
                </p>
            </section>

            <p className="mt-6 max-w-3xl text-xs leading-relaxed text-slate-400">
                Standards references (ERC-1400, ERC-3643, ERC-4337) describe where the
                same control function can be hosted; this sandbox does not deploy
                on-chain contracts. No claim of certification or production readiness
                is made.
            </p>
        </Layout>
    );
}
